import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft, ArrowRight, CheckCircle } from 'lucide-react';
import Alert from '../components/common/Alert';
import api from '../services/api';
import KeyboardDismissView from '../components/common/KeyboardDismissView';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await api.post('/auth/forgot-password', { email });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-[100dvh] bg-surface-50 flex">
        {/* Left Panel - Branding */}
        <div
          className="hidden xl:flex lg:w-1/2 relative overflow-hidden"
          style={{ backgroundColor: 'var(--sidebar-bg)' }}
        >
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-20 left-20 w-72 h-72 bg-income-500 rounded-full blur-3xl" />
            <div
              className="absolute bottom-20 right-20 w-96 h-96 rounded-full blur-3xl"
              style={{ backgroundColor: 'var(--accent-bg)' }}
            />
          </div>

          <div className="relative z-10 flex flex-col justify-center px-12 xl:px-20">
            <div className="mb-8">
              <img
                src="/logo.png"
                alt="Hisab Journey"
                className="w-14 h-14 mb-6 rounded-full"
              />
              <h1 className="text-4xl xl:text-5xl font-bold text-white leading-tight">
                Check your<br />inbox
              </h1>
              <p className="text-surface-400 text-lg mt-4 max-w-md">
                We've sent you instructions to reset your password.
              </p>
            </div>
          </div>
        </div>

        {/* Right Panel - Success Message */}
        <div className="flex-1 flex items-center justify-center p-6 sm:p-12">
          <div className="w-full max-w-md text-center">
            {/* Success Icon */}
            <div
              className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-6"
              style={{ backgroundColor: 'var(--accent-light)' }}
            >
              <CheckCircle className="w-10 h-10" style={{ color: 'var(--accent-bg)' }} />
            </div>

            <h2 className="text-2xl sm:text-3xl font-bold text-surface-900 mb-4">
              Check your email
            </h2>
            <p className="text-surface-500 mb-8">
              If an account exists for <strong className="text-surface-700">{email}</strong>,
              you will receive a password reset link shortly.
            </p>

            <div className="space-y-4">
              <Link
                to="/login"
                className="w-full py-3.5 rounded-xl font-semibold focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all flex items-center justify-center gap-2 group hover:shadow-lg hover:-translate-y-px active:scale-[0.98]"
                style={{
                  backgroundColor: 'var(--accent-bg)',
                  color: 'var(--accent-text)',
                  boxShadow: '0 4px 14px -3px rgba(59, 130, 246, 0.4)'
                }}
              >
                <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                Back to login
              </Link>

              <button
                onClick={() => {
                  setSuccess(false);
                  setEmail('');
                }}
                className="w-full py-3.5 rounded-xl font-medium border border-surface-200 text-surface-700 hover:bg-surface-100 transition-all"
              >
                Try a different email
              </button>
            </div>

            <p className="text-sm text-surface-400 mt-8">
              Didn't receive the email? Check your spam folder or try again in a few minutes.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] bg-surface-50 flex">
      {/* Left Panel - Branding */}
      <div
        className="hidden xl:flex lg:w-1/2 relative overflow-hidden"
        style={{ backgroundColor: 'var(--sidebar-bg)' }}
      >
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-72 h-72 bg-income-500 rounded-full blur-3xl" />
          <div
            className="absolute bottom-20 right-20 w-96 h-96 rounded-full blur-3xl"
            style={{ backgroundColor: 'var(--accent-bg)' }}
          />
        </div>

        <div className="relative z-10 flex flex-col justify-center px-12 xl:px-20">
          <div className="mb-8">
            <img
              src="/logo.png"
              alt="Hisab Journey"
              className="w-14 h-14 mb-6 rounded-full"
            />
            <h1 className="text-4xl xl:text-5xl font-bold text-white leading-tight">
              Reset your<br />password
            </h1>
            <p className="text-surface-400 text-lg mt-4 max-w-md">
              Enter your email and we'll send you a link to reset your password.
            </p>
          </div>

          <div className="space-y-4 mt-8">
            {[
              'Secure password reset',
              'Link expires in 1 hour',
              'Check spam folder if needed'
            ].map((feature, i) => (
              <div key={i} className="flex items-center gap-3 text-surface-300">
                <div className="w-5 h-5 rounded-full bg-income-500/20 flex items-center justify-center">
                  <div className="w-2 h-2 rounded-full bg-income-500" />
                </div>
                {feature}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Panel - Forgot Password Form */}
      <KeyboardDismissView className="flex-1 flex items-center justify-center p-6 sm:p-12 overflow-y-auto safe-area-top">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="xl:hidden text-center mb-6">
            <img
              src="/logo.png"
              alt="Hisab Journey"
              className="w-14 h-14 mx-auto mb-3 rounded-2xl"
            />
          </div>

          {/* Back Link */}
          <Link
            to="/login"
            className="inline-flex items-center gap-2 text-surface-500 hover:text-surface-700 transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to login
          </Link>

          {/* Header */}
          <div className="mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-surface-900">
              Forgot password?
            </h2>
            <p className="text-surface-500 mt-2">
              No worries, we'll send you reset instructions.
            </p>
          </div>

          {error && (
            <Alert
              variant="error"
              message={error}
              onClose={() => setError('')}
            />
          )}

          <form onSubmit={handleSubmit} className="space-y-5 mt-6">
            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-surface-700 mb-2">
                Email address
              </label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-surface-400" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  enterKeyHint="done"
                  className="w-full pl-12 pr-4 py-3 bg-white border border-surface-300 rounded-xl text-surface-900 placeholder-surface-400 focus:outline-none focus:ring-2 focus:ring-surface-900 focus:border-transparent transition-all"
                  required
                />
              </div>
            </div>

            {/* Submit */}
            <button
              type="submit"
              className={`w-full py-3.5 rounded-xl font-semibold focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all flex items-center justify-center gap-2 group ${
                loading ? 'opacity-60 cursor-not-allowed' : 'hover:shadow-lg hover:-translate-y-px active:scale-[0.98]'
              }`}
              style={{
                backgroundColor: 'var(--accent-bg)',
                color: 'var(--accent-text)',
                boxShadow: !loading ? '0 4px 14px -3px rgba(59, 130, 246, 0.4)' : undefined
              }}
              disabled={loading}
            >
              {loading ? (
                'Sending...'
              ) : (
                <>
                  Send reset link
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </>
              )}
            </button>
          </form>

        </div>
      </KeyboardDismissView>
    </div>
  );
}
